import { useState } from 'react';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { convertToTimestamp, getDueDateDaysAway } from '../../utils/dueDate';
import { ApprovalModalBody } from './ApprovalModalBody';
import { ApprovalModalFooter } from './ApprovalModalFooter';
import { ApprovalModalHeader } from './ApprovalModalHeader';
import { ApprovalOperation, ApprovalRules, ApprovalStatus, ApprovalTypes } from '../../types';
import * as trackingConstants from '../../constants/trackingConstants';
import { getErrorSubCategoryFromError } from '../../utils/errorUtils';
import { floatingErrorAlert, operationSuccessAlert } from '../../utils/alertUtils';
import { shouldPrimaryButtonBeDisabled } from '../../utils/approvalModalUtils';
import { getAvailableApprovalRules } from '../../utils/approvalRulesUtils';
import { trackInteraction } from '../../utils/usageTracker';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { useApprovalConditions } from '../../hooks/useApprovalConditions';
import { useGetIsUngated } from '../../hooks/useGetIsUngated';
import { GATES } from '../../constants/auth';
import { withErrorBoundary } from '../../hoc/withErrorBoundary';
import { MAX_APPROVAL_TITLE_LENGTH } from '../../constants/requestModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getInteractionName = (interaction, approvalOperation) => `${interaction}-${approvalOperation}`;
const getModalUse = (approvalOperation, approvalType) => {
  if (approvalOperation === ApprovalOperation.REJECT && (approvalType === ApprovalTypes.DEAL || approvalType === ApprovalTypes.CONNECTED_APP)) {
    return 'danger';
  }
  return undefined;
};
const ApprovalModal = ({
  onClose,
  alertContent,
  showFeedbackSurvey,
  onRequestSuccess: onRequestSuccessCallback
}) => {
  const approval = useApprovalContext();
  const {
    approvalType,
    assetName,
    isLoading,
    currentOperation
  } = approval.meta;
  const {
    userCanSkipThisApproval
  } = useApprovalConditions();
  const getIsUngated = useGetIsUngated();
  const canEditApprovalTitle = getIsUngated(GATES.APPROVAL_DASHBOARD);
  const [approvalTitle, setApprovalTitle] = useState(assetName);
  const [approvalTitleError, setApprovalTitleError] = useState(assetName.length > MAX_APPROVAL_TITLE_LENGTH || !assetName.trim());
  const [requestLoading, setRequestLoading] = useState(false);
  const [note, setNote] = useState('');
  const [approverOwnerIds, setApproverOwnerIds] = useState([]);
  const availableApprovalRules = getAvailableApprovalRules({
    approvalType
  });
  const [approvalRule, setApprovalRule] = useState(availableApprovalRules[0]);
  const [dueDate, setDueDate] = useState(undefined);
  const [dueTime, setDueTime] = useState(undefined);
  const [isDueDateValid, setIsDueDateValid] = useState(true);
  if (currentOperation === null) {
    return null;
  }
  const modalUse = getModalUse(currentOperation, approvalType);
  const trackModalInteraction = (interaction, extra) => {
    const interactionName = getInteractionName(interaction, currentOperation);
    trackInteraction(interactionName, approvalType, extra);
  };
  const onChangeNote = evt => setNote(evt.target.value);
  const onChangeApprovers = value => {
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_CHANGED_APPROVER);
    setApproverOwnerIds(value);
  };
  const onChangeApprovalRule = rule => {
    setApprovalRule(rule);
  };
  const onCancel = () => {
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_CLOSED_WITHOUT_SUBMISSION);
    onClose();
  };
  const onOpenStart = () => {
    setApproverOwnerIds([]);
    setRequestLoading(false);
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_OPENED);
  };
  const getFormSubmissionTrackingExtra = data => {
    const extra = {};
    if (data && 'createdApprovals' in data) {
      extra.approvalId = data.createdApprovals.map(createdApproval => createdApproval.id);
    } else {
      extra.approvalId = data.id;
    }
    if (currentOperation === ApprovalOperation.REQUEST) {
      extra.count = approverOwnerIds.length;
      extra.isSkipped = userCanSkipThisApproval;
      if (dueDate) {
        extra.dueDateDelta = getDueDateDaysAway(dueDate);
      }
    }
    if (currentOperation === ApprovalOperation.APPROVE && data && 'approvalStatus' in data && data.approvalStatus === ApprovalStatus.APPROVED) {
      extra.complete = true;
    }
    if (approvalRule === ApprovalRules.ANY_CAN_APPROVE) {
      extra.rules = trackingConstants.APPROVAL_RULES_ANY_APPROVER;
    } else if (approvalRule === ApprovalRules.NON_SEQUENTIAL) {
      extra.rules = trackingConstants.APPROVAL_RULES_ALL_APPROVERS;
    }
    return extra;
  };
  const onRequestSuccess = data => {
    const extra = getFormSubmissionTrackingExtra(data);
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_FORM_SUBMITTED, extra);
    if (canEditApprovalTitle && approvalTitle !== assetName) {
      trackModalInteraction(trackingConstants.APPROVAL_MODAL_TITLE_CHANGED, extra);
    }
    operationSuccessAlert(approvalType, currentOperation, assetName);
    onClose();
    setRequestLoading(false);
    if (onRequestSuccessCallback) {
      onRequestSuccessCallback(currentOperation);
    }
  };
  const onRequestError = errorResponse => {
    setRequestLoading(false);
    const errorSub = getErrorSubCategoryFromError(errorResponse);
    floatingErrorAlert(approvalType, errorSub);
    onClose();
  };
  const onSubmitForm = evt => {
    evt.preventDefault();
    setRequestLoading(true);
    if (currentOperation === ApprovalOperation.REQUEST) {
      let dueDateTimestamp = undefined;
      if (dueDate) {
        dueDateTimestamp = convertToTimestamp(dueDate, dueTime);
      }
      approval.api.create(note, approverOwnerIds, approvalRule, dueDateTimestamp, canEditApprovalTitle ? approvalTitle : undefined).then(onRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.CANCEL || currentOperation === ApprovalOperation.RECALL) {
      approval.api.cancel(note).then(onRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.APPROVE) {
      approval.api.approve(note).then(onRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.REJECT) {
      approval.api.reject(note).then(onRequestSuccess).catch(onRequestError);
    }
  };
  const disablePrimaryButton = shouldPrimaryButtonBeDisabled(currentOperation, note, approverOwnerIds.length) || !isDueDateValid || canEditApprovalTitle && approvalTitleError;
  return /*#__PURE__*/_jsxs(Modal, {
    role: "dialog",
    "data-test-id": `product-approvals-approval-modal-${currentOperation}`,
    width: 600,
    onOpenStart: onOpenStart,
    use: modalUse,
    children: [approval && isLoading && /*#__PURE__*/_jsx(LoadingOverlay, {
      contextual: true
    }), /*#__PURE__*/_jsxs(Form, {
      onSubmit: onSubmitForm,
      children: [/*#__PURE__*/_jsx(ApprovalModalHeader, {
        approvalType: approvalType,
        approvalOperation: currentOperation,
        onCancel: onCancel
      }), /*#__PURE__*/_jsx(ApprovalModalBody, {
        alertContent: alertContent,
        currentOperation: currentOperation,
        approverOwnerIds: approverOwnerIds,
        availableApprovalRules: availableApprovalRules,
        isLoading: requestLoading,
        note: note,
        onChangeApprovers: onChangeApprovers,
        onChangeApprovalRule: onChangeApprovalRule,
        onChangeNote: onChangeNote,
        dueDate: dueDate,
        setDueDate: setDueDate,
        dueTime: dueTime,
        setDueTime: setDueTime,
        isDueDateValid: isDueDateValid,
        setIsDueDateValid: setIsDueDateValid,
        approvalTitle: approvalTitle,
        canEditApprovalTitle: canEditApprovalTitle,
        onChangeApprovalTitle: setApprovalTitle,
        approvalTitleError: approvalTitleError,
        setApprovalTitleError: setApprovalTitleError
      }), /*#__PURE__*/_jsx(ApprovalModalFooter, {
        approvalType: approvalType,
        approvalOperation: currentOperation,
        disablePrimaryButton: disablePrimaryButton,
        onCancel: onCancel,
        isLoading: requestLoading,
        showFeedbackSurvey: showFeedbackSurvey,
        buttonUse: modalUse
      })]
    })]
  });
};
const ApprovalModalWithErrorBoundary = withErrorBoundary(ApprovalModal, 'ApprovalModal');
export { ApprovalModalWithErrorBoundary as ApprovalModal };